import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig } from "../../../services/api-config";
import { API_USER_ROUTER } from "../../../services/api-config";
import { ATTENDANCE_MODE } from "../../../constants/status";

export enum UserStatusCode {
  INACTIVE = 0,
  ACTIVE = 1,
}

export interface User {
  totp_qr_code: string | undefined;
  password: string | undefined;
  id: number;
  username?: string;
  department_id?: number;
  name: string;
  email: string;
  phone: string;
  privilege_group_id?: number;
  privilege_group_name?: string;
  address?: string;
  avatar?: string;
  gender?: number;
  joining_date?: string;
  birthday?: string;
  id_card_number?: string;
  identity_card?: string;
  status: UserStatusCode;
  created_at: string;
  updated_at: string;
  branches?: Array<{
    id: number;
    name: string;
    address?: string;
    phone?: string;
    status?: number;
  }>;
  company_id?: number;
  log_company_id?: number;
  is_all_branch?: number; // 1 = tất cả đại lý, 0 = chỉ một số
  is_2fa?: number;        // 1 = bật, 0 = tắt
  department_name?: string;
  customer_ids?: string;
  /** Ghi đè chế độ chấm công của bộ phận riêng cho NV này. NULL/undefined = kế thừa bộ phận. */
  attendance_mode?: ATTENDANCE_MODE | null;
}

export interface UserListFilters {
  status?: number; // 1 active, 0 inactive, -1 all
  key_search?: string;
  department_id?: number;
  branch_id?: number; // lọc theo chi nhánh (trong phạm vi chi nhánh user trực thuộc)
  page?: number;
  limit?: number;
}

export interface UserListResponse {
  list: User[];
  total_record?: number;
  limit?: number;
}

export interface CountTabsResponse {
  active: number;
  inactive: number;
}

export interface CreateUserPayload {
  username: string;
  department_id?: number;
  privilege_group_id?: number;
  name: string;
  email: string;
  phone: string;
  password: string;
  address?: string;
  avatar?: string;
  gender?: number;
  joining_date?: string;
  birthday?: string;
  id_card_number?: string;
  status?: UserStatusCode;
  attendance_mode?: ATTENDANCE_MODE | null;
}

export interface UpdateUserPayload {
  username?: string;
  department_id?: number;
  privilege_group_id?: number;
  name: string;
  email: string;
  phone: string;
  password?: string;
  address?: string;
  avatar?: string;
  gender?: number;
  joining_date?: string;
  birthday?: string;
  id_card_number?: string;
  status?: UserStatusCode;
  attendance_mode?: ATTENDANCE_MODE | null;
}

export interface ChangeStatusPayload {
  status: UserStatusCode;
}

export interface ResetPasswordPayload {
  id: number;
}

const UserService = {
  list: () => {
    const { request, baseResponse, loading } = useFetchData.Get<UserListResponse>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (params: UserListFilters = { status: -1, key_search: "" }) => {
        const queryParams = {
          status: params.status,
          key_search: params.key_search,
          department_id: params.department_id,
          branch_id: params.branch_id,
          page: params.page ?? 1,
          limit: params.limit ?? 20,
        };
        return request({
          endPoint: API_USER_ROUTER.API_LIST,
          data: queryParams,
        });
      },
      response: baseResponse,
      loading: loading,
    }
  },

  countTabs: () => {
    const { request, baseResponse, loading } = useFetchData.Get<{
      total: number;
      total_active: number;
      total_locked: number;
    }>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (params: { key_search?: string; department_id?: number; branch_id?: number } = {}) =>
        request({
          endPoint: API_USER_ROUTER.API_COUNT_TABS,
          data: {
            key_search: params.key_search,
            department_id: params.department_id,
            branch_id: params.branch_id,
          },
        }),
      response: baseResponse,
      loading: loading,
    };
  },
  create: () => {
    const { request, baseResponse, loading } = useFetchData.Post<User>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (payload: CreateUserPayload) =>
        request({
          endPoint: API_USER_ROUTER.API_CREATE,
          data: payload,
        }),
      response: baseResponse,
      loading: loading,
    }
  },

  update: () => {
    const { request, baseResponse, loading } = useFetchData.Post<User>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return{
      fetch: (id: number, payload: UpdateUserPayload) =>
        request({
          endPoint: API_USER_ROUTER.API_UPDATE(id),
          data: payload,
        }),
      response: baseResponse,
      loading: loading
    }
  },

  changeStatus: () => {
    const { request, baseResponse, loading } = useFetchData.Post<User>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (id: number, payload: ChangeStatusPayload) =>
        request({
          endPoint: API_USER_ROUTER.API_CHANGE_STATUS(id),
          data: payload,
        }),
      response: baseResponse,
      loading: loading,
    }
  },

  detail: () => {
    const { request, baseResponse, loading } = useFetchData.Get<User>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({
          endPoint: API_USER_ROUTER.API_DETAIL(id),
        }),
      response: baseResponse,
      loading: loading,
    }
  },

  resetPassword: () => {
    const { request, baseResponse, loading } = useFetchData.Post<User>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({
          endPoint: API_USER_ROUTER.API_RESET_PASSWORD(id),
          data: {},
        }),
      response: baseResponse,
      loading: loading,
    }
  },

  assignCustomers: () => {
    const { request, baseResponse, loading } = useFetchData.Post<any>({
      projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
    });
    return {
      fetch: (id: number, customer_ids: string) =>
        request({
          endPoint: API_USER_ROUTER.API_ASSIGN_CUSTOMER(id),
          data: { customer_ids },
        }),
      response: baseResponse,
      loading: loading,
    };
  },
};

export default UserService;
